import React, { useEffect, useState, useContext } from 'react';
import { AppContext, useProduct, COMMON_CONSTANTS } from '@nile/components';
import { TextStyle, Dropdown, DisplayText } from '@superops/ui-design-system';
import { DepartmentSelect } from '@nile/itsm/src/pages/components/DepartmentSelect';
import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage } from 'react-intl';

interface CategoryAndDepartmentProps {
  isAdvancedHierarchy?: boolean;
  policySetMapping: any;
  handlePolicyCategoryChange: (value: string) => void;
  handleDepartmentChange: (department: any) => void;
  selectedDepartment: any;
  selectedPolicyCategory?: string | null;
  selectedOS?: string;
  isRequiredDeviceCategory?: boolean;
  isMDMContext?: boolean;
}

function CategoryAndDepartment({
  isAdvancedHierarchy,
  policySetMapping,
  handlePolicyCategoryChange,
  handleDepartmentChange,
  selectedDepartment,
  selectedPolicyCategory = null,
  selectedOS = 'WINDOWS',
  isRequiredDeviceCategory = false,
  isMDMContext = false
}: CategoryAndDepartmentProps) {
  const appContext = useContext(AppContext);
  const isITProduct = useProduct(COMMON_CONSTANTS.IT);

  //Check if department selection should be displayed
  const showDepartmentSelect = isMDMContext
    ? isITProduct
    : appContext?.user?.rmmAppProperties?.betaAgentBasedFeatures
        ?.silentInstallerArgs && isITProduct;

  const [updatedPolicySetMapping, setUpdatedPolicySetMapping] = useState<any[]>(
    []
  );
  const [
    internalSelectedPolicyCategory,
    setInternalSelectedPolicyCategory
  ] = useState<string | null>(selectedPolicyCategory);

  useEffect(() => {
    if (policySetMapping && selectedOS) {
      getPolicySetMappingOptions();
    }
  }, [policySetMapping, selectedOS]);

  useEffect(() => {
    setInternalSelectedPolicyCategory(selectedPolicyCategory);
  }, [selectedPolicyCategory]);

  const onPolicyCategoryChange = (value: string) => {
    setInternalSelectedPolicyCategory(value);
    handlePolicyCategoryChange(value);
  };

  const getPolicySetMappingOptions = () => {
    if (!policySetMapping || !isAdvancedHierarchy) {
      return;
    }
    const selectedOs = selectedOS.toLowerCase();

    const tempUpdatedPolicySetMapping =
      policySetMapping[selectedOs]?.map((item: any) => {
        return {
          label: item.policyCategory.name,
          value: item.policyCategory.policyCategoryId
        };
      }) || [];

    if (
      tempUpdatedPolicySetMapping.length > 0 &&
      !internalSelectedPolicyCategory
    ) {
      onPolicyCategoryChange(tempUpdatedPolicySetMapping[0]?.value);
    }
    setUpdatedPolicySetMapping(tempUpdatedPolicySetMapping);
  };

  // Only render if either isAdvancedHierarchy or showDepartmentSelect is true
  if (!isAdvancedHierarchy && !showDepartmentSelect) {
    return null;
  }

  return (
    <div
      className={`flex bg-white  ${
        !isMDMContext ? 'mb-15 p10 center g50' : 'col g10'
      }`}
    >
      {isAdvancedHierarchy && (
        <div className={`flex center ${isMDMContext ? 'g20' : 'mr-15'}`}>
          <div className="flex actr g10">
            <div className="flex actr">
              <SVGIcon name="CategorySubcategory" />
            </div>
            {isMDMContext ? (
              <div className="w200">
                <DisplayText size="large" isRequired={isRequiredDeviceCategory}>
                  <FormattedMessage id="admin.deviceCategory.header" />
                </DisplayText>
              </div>
            ) : (
              <DisplayText
                variant="subdued"
                size="large"
                weight="Regular"
                isRequired={isRequiredDeviceCategory}
              >
                <FormattedMessage id="admin.deviceCategory.header" />
              </DisplayText>
            )}
          </div>
          <div className={isMDMContext ? 'w300' : 'w180 ml-15'}>
            <Dropdown
              noBorder
              value={updatedPolicySetMapping.find(
                (el) => el.value === internalSelectedPolicyCategory
              )}
              options={updatedPolicySetMapping}
              onChange={(val) => {
                onPolicyCategoryChange(val.value);
              }}
              className="bg-none"
              classNamePrefix="value_selector"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>
        </div>
      )}
      {showDepartmentSelect && (
        <div className={`flex center ${isMDMContext ? 'g20' : ''}`}>
          <div className="flex actr g10">
            <div className="flex actr">
              <SVGIcon name="Department" />
            </div>
            {isMDMContext ? (
              <div className="w200">
                <DisplayText size="large">
                  <FormattedMessage id="common.select.department" />
                </DisplayText>
              </div>
            ) : (
              <TextStyle variation="subdued" weight="Regular">
                <FormattedMessage id="common.select.department" />
              </TextStyle>
            )}
          </div>

          <div className={isMDMContext ? 'w300' : 'w180 ml-15'}>
            <DepartmentSelect
              onChange={handleDepartmentChange}
              department={selectedDepartment}
              showIcon={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryAndDepartment;
